import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router';
import { useMedia } from 'react-use-media';

import Typeform, {
  ProgressBarVariant,
  Transition,
} from '~/App/Components/Typeform';
import AttachmentCardContainer from './AttachmentCardContainer';

import RespondentContext from '../Context/Respondent';
import TestimonialContext from '~/App/Modules/Testimonials/Context/TestimonialContext';
import QuestionnaireProvider from '~/App/Modules/Testimonials/Components/Common/QuestionnaireProvider';
import ConsentCard from '~/App/Modules/Testimonials/Components/Common/ConsentCard';
import {
  submitRespondentResponse,
  SubmitRespondentResponseParams,
} from '../Api/TestimonialRespondent';
import { alertAsync } from '~/App/Components/Modals/Alert';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  TESTIMONIAL_RESPONSE_FORM_CONSENT,
  TESTIMONIAL_RESPONSE_SUBMIT,
} from '~/generated/AnalyticsTypes';
import NavAction from '~/App/Components/Typeform/NavAction';
import useTypeformNavContext, {
  TypeformNavProvider,
} from '~/Share/TestimonialLandingPage/Context/TypeformNav';
import { useFeatures } from '~/react-hooks/use-features';
import {
  QuestionnaireResponseProvider,
  TestimonialResponseState,
  useTestimonialResponse,
} from '../Context/TestimonialResponse';
import useUnloadPagePrompt from '~/react-hooks/useUnloadPagePrompt';
import {
  addUtmToLink,
  UTM_CAMPAIGN_TESTIMONIALS_PLG,
  UTM_MEDIUM_POWERED_BY_BADGE,
  UTM_SOURCE_TESTIMONIAL_REQUEST_PAGE,
} from '~/utils/add-utm-to-link';

export const landingPageBaseRoute = '/te/:name/:id';

function ScrollableContent() {
  const { state: typeformNavState } = useTypeformNavContext();
  const { respondent } = useContext(RespondentContext);
  const testimonial = useContext(TestimonialContext);
  const { shouldHideBonjoroTestimonialBranding } = useFeatures();
  const showBonjoroBadge = !shouldHideBonjoroTestimonialBranding;
  const { state: testimonialResponseState } = useTestimonialResponse();
  const [submitting, setSubmitting] = useState(false);

  const { font_family, cta_corner_radius, primary_color } =
    testimonial?.metadata?.style ?? {};

  const hasTestimonialResponseUnsubmittedChange = useMemo(() => {
    return (
      typeof testimonialResponseState.find((response) => {
        return (
          response.state === TestimonialResponseState.Uploading ||
          response.state === TestimonialResponseState.Changed
        );
      }) !== 'undefined'
    );
  }, [testimonialResponseState]);

  const analytics = useAnalytics();
  const history = useHistory();

  const redirectToThankYouPage = () =>
    history.push(`${history.location.pathname}/submitted`);

  const [hasConsent, setHasConsent] = useState<boolean>(
    respondent?.isSubmitted() ?? false
  );

  useEffect(() => {
    if (respondent.isSubmitted()) {
      redirectToThankYouPage();
    }
  }, [respondent]);

  const { mutateAsync: submitRespondentResponseMutation } = useMutation(
    (submitRespondentResponseParams: SubmitRespondentResponseParams) =>
      submitRespondentResponse(submitRespondentResponseParams)
  );

  useEffect(() => {
    if (hasConsent) {
      analytics.track(TESTIMONIAL_RESPONSE_FORM_CONSENT);
    }
  }, [hasConsent]);

  useEffect(() => {
    const submitTestimonial = async () => {
      // Wait until all uploading response is completed before submitting the testimonial.
      if (submitting && !hasTestimonialResponseUnsubmittedChange) {
        setSubmitting(false);

        const testimonialUuid = testimonial.uuid;
        const respondentUuid = respondent?.get('uuid') as string;

        await submitRespondentResponseMutation({
          testimonialUuid,
          respondentUuid,
          hasConsent,
        });

        void analytics.track(TESTIMONIAL_RESPONSE_SUBMIT, {
          project_owner: testimonial.user.email,
        });

        redirectToThankYouPage();
      }
    };

    submitTestimonial().catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
      alertAsync(
        <>
          <h1 className='my-0'>Something went wrong...</h1>
          <hr />
          <h2 className='mb-8'>Please reload the page and try again.</h2>
        </>
      );
    });
  }, [submitting, hasTestimonialResponseUnsubmittedChange]);

  const questionCards = useMemo(() => {
    if (!respondent) return [];

    const questions = respondent.getQuestions();
    const hideQuestionHeader = questions.size === 1;

    return questions
      .map((question, index) => (
        <QuestionnaireProvider
          key={index}
          questionId={question?.get('uuid') as string}
          hideQuestionHeader={hideQuestionHeader}
        />
      ))
      .toArray();
  }, [respondent]);

  const attachmentCards = useMemo(() => {
    if (!respondent) return [];

    const showPositionField = respondent.getHasPositionTitle();
    const requests = respondent.getRequests();
    const files = requests.get('files').toArray();
    const links = requests.get('links').toArray();

    if (!showPositionField && [...files, ...links].length === 0) return [];

    const attachmentCard = (
      <AttachmentCardContainer
        showPositionField={showPositionField}
        requests={requests}
      />
    );

    return [attachmentCard];
  }, [respondent]);

  const consentCard = (
    <ConsentCard
      checked={hasConsent}
      onCheckboxClicked={() => setHasConsent(!hasConsent)}
      onSubmitClick={() => setSubmitting(true)}
      loading={submitting}
    />
  );

  const typeformCards = [...questionCards, ...attachmentCards, consentCard];

  const initialPageIndex = respondent.isSubmitted()
    ? typeformCards.length - 1
    : 0;

  //Blocks respondent from leaving the page if there's uploading or unsubmitted change
  useUnloadPagePrompt(hasTestimonialResponseUnsubmittedChange);

  const isMobile = useMedia({
    maxWidth: 767,
  });

  const onBonjoroBadgeClick = () => {
    window.location.href = addUtmToLink(
      'https://www.bonjoro.com/testimonials',
      {
        utm_source: UTM_SOURCE_TESTIMONIAL_REQUEST_PAGE,
        utm_medium: UTM_MEDIUM_POWERED_BY_BADGE,
        utm_campaign: UTM_CAMPAIGN_TESTIMONIALS_PLG,
      }
    );
  };

  return (
    <>
      <Typeform
        className='!max-w-full z-50 !h-full'
        progressBarVariant={ProgressBarVariant.FullWidth}
        showBonjoroBadge={showBonjoroBadge}
        onBonjoroBadgeClick={onBonjoroBadgeClick}
        defaultIndex={initialPageIndex}
        transition={isMobile ? Transition.Default : Transition.FadeIn}
      >
        {typeformCards}
      </Typeform>
      <NavAction
        className='md:!hidden'
        onNextClick={typeformNavState.onNextClick}
        onPreviousClick={typeformNavState.onPreviousClick}
        nextDisabled={typeformNavState.nextDisabled}
        previousDisabled={typeformNavState.previousDisabled}
        fontFamily={font_family}
        ctaCornerRadius={cta_corner_radius}
        ctaColor={primary_color}
      />
    </>
  );
}

export default function TestimonialPageContainer() {
  return (
    <TypeformNavProvider>
      <QuestionnaireResponseProvider>
        <ScrollableContent />
      </QuestionnaireResponseProvider>
    </TypeformNavProvider>
  );
}
